import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import { Modal, FormField, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ROLES, USER_TYPES, USER_STATUSES, COLORS, formatLabel } from '../../utils/constants';

const C = COLORS;

const UsersPage = () => {
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const roleOptions = isManager ? ['editor', 'user'] : ROLES;

  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const res = await usersAPI.getAll({ page, limit: 10, ...filter });
      setData(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
  }, [page, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.first_name) e.first_name = 'Required';
    if (!form.last_name) e.last_name = 'Required';
    if (!form.email) e.email = 'Required';
    if (!modal?.id && !form.password) e.password = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (modal?.id) {
        await usersAPI.update(modal.id, form);
      } else {
        await usersAPI.create(form);
      }
      setModal(null);
      setForm({});
      fetchData();
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to save' });
    } finally { setLoading(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete user ${row.first_name} ${row.last_name}?`)) return;
    try { await usersAPI.delete(row.id); fetchData(); } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const openEdit = (row) => {
    setForm({ first_name: row.first_name, last_name: row.last_name, email: row.email, phone: row.phone, role: row.role, user_type: row.user_type, status: row.status });
    setModal({ id: row.id });
    setErrors({});
  };

  const columns = [
    { key: 'first_name', label: 'User', render: (_, r) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          {r.first_name?.[0]}{r.last_name?.[0]}
        </div>
        <span style={{ fontWeight: 600, color: C.g800 }}>{r.first_name} {r.last_name}</span>
      </div>
    )},
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role', render: v => (
      <span style={{ fontSize: 12, fontWeight: 600, color: v === 'admin' ? C.primary : C.g600, background: v === 'admin' ? C.primaryGhost : C.g100, padding: '3px 10px', borderRadius: 6 }}>{formatLabel(v)}</span>
    )},
    { key: 'user_type', label: 'User Type' },
    { key: 'created_at', label: 'Joined', render: v => new Date(v).toLocaleDateString() },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  return (
    <AppLayout title="User Management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>Users</h2>
        <PrimaryButton onClick={() => { setForm({ role: isManager ? 'user' : 'user' }); setErrors({}); setModal({}); }} icon={<UserPlus size={18} />}>Add User</PrimaryButton>
      </div>

      <DataTable columns={columns} data={data} page={page} totalPages={totalPages}
        onPageChange={setPage} searchPlaceholder="Search users..."
        onSearch={s => setFilter(p => ({ ...p, search: s }))}
        filters={[
          { label: 'Role', options: ['All', ...(isManager ? ['Editor', 'User'] : ROLES.map(r => formatLabel(r)))], value: filter.role ? formatLabel(filter.role) : 'All', onChange: v => setFilter(p => ({ ...p, role: v === 'All' ? undefined : v.toLowerCase() })) },
          { label: 'User Type', options: ['All', ...USER_TYPES], value: filter.user_type || 'All', onChange: v => setFilter(p => ({ ...p, user_type: v === 'All' ? undefined : v })) }
        ]}
        onEdit={openEdit} onDelete={user?.role === 'admin' ? handleDelete : undefined}
      />

      {modal && (
        <Modal title={modal.id ? 'Edit User' : 'Add New User'} onClose={() => setModal(null)}>
          {errors.submit && <div style={{ padding: '10px 14px', background: C.dangerLight, borderRadius: 10, marginBottom: 16, fontSize: 13, color: C.danger }}>{errors.submit}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="First Name" name="first_name" value={form.first_name} onChange={handleChange} error={errors.first_name} required />
            <FormField label="Last Name" name="last_name" value={form.last_name} onChange={handleChange} error={errors.last_name} required />
          </div>
          <FormField label="Email" name="email" type="email" value={form.email} onChange={handleChange} error={errors.email} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Role" name="role" value={form.role || (isManager ? 'user' : '')} onChange={handleChange} options={roleOptions} />
            <FormField label="User Type" name="user_type" value={form.user_type} onChange={handleChange} options={USER_TYPES} />
          </div>
          <FormField label="Phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" />
          {!modal.id && <FormField label="Password" name="password" type="password" value={form.password} onChange={handleChange} error={errors.password} required />}
          {modal.id && <FormField label="Status" name="status" value={form.status} onChange={handleChange} options={USER_STATUSES} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : modal.id ? 'Update' : 'Create'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default UsersPage;
